import { Signal, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { AlertController } from '@ionic/angular/standalone';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ViewportService } from '@core/services/viewport.service';

/**
 * Interface for components that can have unsaved changes.
 * Implement this in components that use TaskDetailFacade or similar auto-save patterns.
 */
export interface HasUnsavedChanges {
  /** Signal indicating if a save operation is in progress */
  isSaving: Signal<boolean>;
  /** Optional signal indicating if there are pending changes not yet saved */
  hasUnsavedChanges?: Signal<boolean>;
  /**
   * Optional: flush any debounced/pending writes immediately and resolve
   * when the server has acknowledged (`true`) or with `false` on failure.
   * On mobile, the guard calls this first so the user never has to tap "Save".
   */
  flushPending?: () => Promise<boolean>;
  /**
   * Optional: drop any pending (not yet flushed) changes. Called when the
   * user explicitly chooses "Discard" in the unsaved-changes alert.
   */
  rollbackPending?: () => void;
}

/**
 * Guard to prevent navigation when there are unsaved changes or active save
 * operations.
 *
 * - **Mobile**: tries to `flushPending()` silently first ("always save"). If
 *   that fails or there is still an in-flight save, shows an Ionic
 *   `AlertController` dialog with "Hier bleiben" / "Verwerfen" actions.
 * - **Desktop**: shows an NgZorro `NzModalService.confirm` modal (no
 *   `window.confirm`).
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = async (
  component,
): Promise<boolean> => {
  if (!component?.isSaving) {
    return true;
  }

  const viewport = inject(ViewportService);
  const isMobile = viewport.isMobile();

  // On mobile, try to save silently first.
  if (isMobile && component.flushPending) {
    try {
      const ok = await component.flushPending();
      if (ok && !component.isSaving() && !component.hasUnsavedChanges?.()) {
        return true;
      }
    } catch {
      // fall through to alert
    }
  }

  // If nothing pending and nothing in flight, just leave.
  if (!component.isSaving() && !component.hasUnsavedChanges?.()) {
    return true;
  }

  if (isMobile) {
    return confirmMobile(component);
  }
  return confirmDesktop(component);
};

async function confirmMobile(component: HasUnsavedChanges): Promise<boolean> {
  const alertCtrl = inject(AlertController);
  const hasChanges = component.hasUnsavedChanges?.() ?? false;
  const message = component.isSaving()
    ? 'Änderungen werden gerade gespeichert. Wenn du jetzt gehst, können Änderungen verloren gehen. Trotzdem verlassen?'
    : hasChanges
      ? 'Du hast ungespeicherte Änderungen. Wenn du jetzt gehst, gehen sie verloren. Trotzdem verlassen?'
      : 'Diese Seite wirklich verlassen?';

  const alert = await alertCtrl.create({
    header: 'Ungespeicherte Änderungen',
    message,
    backdropDismiss: false,
    buttons: [
      { text: 'Hier bleiben', role: 'cancel' },
      { text: 'Verwerfen', role: 'destructive' },
    ],
  });
  await alert.present();
  const { role } = await alert.onDidDismiss();
  const leave = role === 'destructive';
  if (leave) {
    component.rollbackPending?.();
  }
  return leave;
}

function confirmDesktop(component: HasUnsavedChanges): Promise<boolean> {
  const modal = inject(NzModalService);
  const hasChanges = component.hasUnsavedChanges?.() ?? false;
  const content = component.isSaving()
    ? 'Changes are currently being saved. If you leave now, some changes may be lost.'
    : hasChanges
      ? 'You have unsaved changes. If you leave now, these changes will be lost.'
      : 'Leave this page?';

  return new Promise<boolean>((resolve) => {
    modal.confirm({
      nzTitle: 'Unsaved changes',
      nzContent: content,
      nzOkText: 'Discard',
      nzOkDanger: true,
      nzCancelText: 'Stay',
      nzOnOk: () => {
        component.rollbackPending?.();
        resolve(true);
      },
      nzOnCancel: () => resolve(false),
    });
  });
}
